import React from 'react';
import { Wallet, CheckCircle2, AlertOctagon } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { BudgetSummary } from '@/types/domain';

interface BudgetSummaryWidgetProps {
  budget: BudgetSummary | null;
  requestAmount: number;
  currency?: string;
  horizontal?: boolean;
}

export const BudgetSummaryWidget: React.FC<BudgetSummaryWidgetProps> = ({
  budget,
  requestAmount,
  horizontal = true,
}) => {
  if (!budget) return null;

  const totalUsedAfterRequest = budget.budgetUsed + budget.pendingRequests + requestAmount;
  const isOverBudget = totalUsedAfterRequest > budget.budgetLimit;
  const remainingAfterRequest = budget.availableBudget - requestAmount;
  const utilizationPct = Math.min(100, Math.round((totalUsedAfterRequest / budget.budgetLimit) * 100));

  const formatRupees = (amount: number) =>
    '₹' + Math.round(amount).toLocaleString('en-IN');

  return (
    <Card className="border border-border rounded-2xl shadow-2xs overflow-hidden font-poppins">
      <CardHeader className="py-3 px-4 border-b border-border/60 bg-muted/20">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2.5 flex-wrap">
          <CardTitle className="text-xs font-bold uppercase tracking-wider flex items-center gap-2 text-foreground min-w-0">
            <Wallet className="h-4 w-4 text-primary shrink-0" />
            <span className="truncate">Cost Center Budget Impact ({budget.costCenterCode})</span>
          </CardTitle>
          {isOverBudget ? (
            <span className="text-[11px] font-bold text-destructive bg-destructive/10 px-2.5 py-1 rounded-lg flex items-center gap-1.5 border border-destructive/20 shrink-0 whitespace-nowrap shadow-2xs">
              <AlertOctagon className="h-3.5 w-3.5 shrink-0" /> Budget Exception Required
            </span>
          ) : (
            <span className="text-[11px] font-bold text-emerald-600 dark:text-emerald-400 bg-emerald-500/10 px-2.5 py-1 rounded-lg flex items-center gap-1.5 border border-emerald-500/20 shrink-0 whitespace-nowrap shadow-2xs">
              <CheckCircle2 className="h-3.5 w-3.5 shrink-0" /> Budget Approved & Available
            </span>
          )}
        </div>
      </CardHeader>
      <CardContent className="p-4 space-y-4 text-xs">
        {/* Metric Tiles - Horizontal 4 Columns */}
        <div className={`grid gap-3 ${horizontal ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4' : 'grid-cols-2'}`}>
          <div className="p-3.5 rounded-xl bg-muted/30 border border-border/70 space-y-1">
            <p className="text-[10px] uppercase font-bold text-foreground-muted">Annual Budget</p>
            <p className="text-base font-extrabold text-foreground font-mono">{formatRupees(budget.budgetLimit)}</p>
            <p className="text-[10px] text-foreground-secondary">Fiscal Year Limit</p>
          </div>

          <div className="p-3.5 rounded-xl bg-muted/30 border border-border/70 space-y-1">
            <p className="text-[10px] uppercase font-bold text-foreground-muted">Committed Spend</p>
            <p className="text-base font-extrabold text-foreground font-mono">{formatRupees(budget.budgetUsed + budget.pendingRequests)}</p>
            <p className="text-[10px] text-foreground-secondary">Prior Approved Commitments</p>
          </div>

          <div className="p-3.5 rounded-xl bg-primary/5 border border-primary/20 space-y-1">
            <p className="text-[10px] uppercase font-bold text-primary">This Requisition</p>
            <p className="text-base font-extrabold text-primary font-mono">{formatRupees(requestAmount)}</p>
            <p className="text-[10px] text-primary/80">Pending Allocation</p>
          </div>

          <div className={`p-3.5 rounded-xl border space-y-1 ${remainingAfterRequest < 0 ? 'bg-destructive/5 border-destructive/20' : 'bg-emerald-500/5 border-emerald-500/20'}`}>
            <p className="text-[10px] uppercase font-bold text-foreground-muted">Remaining Buffer</p>
            <p className={`text-base font-extrabold font-mono ${remainingAfterRequest < 0 ? 'text-destructive' : 'text-emerald-600 dark:text-emerald-400'}`}>
              {formatRupees(Math.max(0, remainingAfterRequest))}
            </p>
            <p className={`text-[10px] font-semibold ${remainingAfterRequest < 0 ? 'text-destructive' : 'text-emerald-600 dark:text-emerald-400'}`}>
              {remainingAfterRequest < 0 ? 'Over Allocated' : 'Post-Approval Available'}
            </p>
          </div>
        </div>

        {/* Utilization Bar */}
        <div className="space-y-1.5 pt-1">
          <div className="flex justify-between text-[11px] font-semibold text-foreground-secondary">
            <span>Budget Commitment: <strong className="text-foreground">{utilizationPct}%</strong></span>
            <span className="font-mono text-foreground-muted">Cap: {formatRupees(budget.budgetLimit)}</span>
          </div>
          <div className="h-2 w-full rounded-full bg-muted overflow-hidden border border-border/40">
            <div
              className={`h-full transition-all duration-500 rounded-full ${
                isOverBudget
                  ? 'bg-destructive'
                  : utilizationPct > 75
                  ? 'bg-amber-500'
                  : 'bg-emerald-500'
              }`}
              style={{ width: `${utilizationPct}%` }}
            />
          </div>
        </div>

        {/* Ledger Rule Tag */}
        <p className="text-[10px] text-foreground-secondary border-t border-border/50 pt-2.5 leading-relaxed font-mono">
          ✓ Verified in SAP Cost Center Accounting for {budget.costCenterCode}
        </p>
      </CardContent>
    </Card>
  );
};
